import { useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { v4 as uuid } from 'uuid';

import { formatDuration } from '@/lib/format-duration';
import { ChatHttpError } from '@/services/chatApi';
import type { ChatResponse } from '@/services/chatApi';
import { chatStream } from '@/services/chatStream';
import { resetSession } from '@/services/session';

import {
  useChatApi,
  useChatBranding,
  useChatConfig,
  useChatStore,
  useChatStoreApi,
} from '../ChatProvider';
import type { PodcastMessage, TextMessage, UserAudioMessage } from '../state/types';
import { makeWelcomeMessage } from '../state/welcome';

interface UseChatActionsResult {
  sendText: (text: string) => Promise<void>;
  sendAudio: (blob: Blob, mimeType: string, durationMs: number) => Promise<void>;
  generatePodcast: (sourceMessageId: string) => Promise<void>;
  clear: () => void;
}

export function useChatActions(): UseChatActionsResult {
  const api = useChatApi();
  const config = useChatConfig();
  const branding = useChatBranding();
  const store = useChatStoreApi();

  const append = useChatStore((s) => s.appendMessage);
  const patch = useChatStore((s) => s.patchMessage);
  const remove = useChatStore((s) => s.removeMessage);
  const setStreaming = useChatStore((s) => s.setStreaming);
  const setInputText = useChatStore((s) => s.setInputText);
  const setPodcastGen = useChatStore((s) => s.setPodcastGen);
  const showErrorOverlay = useChatStore((s) => s.showErrorOverlay);
  const clearChat = useChatStore((s) => s.clearChat);

  const ctrlRef = useRef<AbortController | null>(null);
  const podcastCtrlRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    const podcastCtrls = podcastCtrlRef.current;
    return () => {
      ctrlRef.current?.abort();
      podcastCtrls.forEach((c) => {
        c.abort();
      });
      podcastCtrls.clear();
    };
  }, []);

  const sendText = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text) return;

      const userMsg: TextMessage = {
        id: uuid(),
        sender: 'user',
        kind: 'text',
        text,
        createdAt: Date.now(),
      };
      const botMsg: TextMessage = {
        id: uuid(),
        sender: 'bot',
        kind: 'text',
        text: '',
        createdAt: Date.now(),
        isStreaming: true,
        isTyping: true,
      };

      append(userMsg);
      append(botMsg);
      setInputText('');
      setStreaming(botMsg.id, 'streaming');

      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;

      let streamSucceeded = false;

      if (config.enableStreaming) {
        try {
          let target = '';
          let revealedLen = 0;
          let receivedAnyPartial = false;
          let serverDone = false;
          let hideActionsOnDone = false;
          // eslint-disable-next-line @typescript-eslint/no-empty-function -- reassigned synchronously by the Promise executor on the next line
          let drainResolve: () => void = () => {};
          const drainPromise = new Promise<void>((resolve) => {
            drainResolve = resolve;
          });

          const smoothingMs = config.streamSmoothingMs;
          let rafId: number | null = null;
          let lastFrameTime = 0;

          const tick = (now: number): void => {
            if (ctrl.signal.aborted) {
              rafId = null;
              drainResolve();
              return;
            }
            const dt = now - lastFrameTime;
            lastFrameTime = now;
            const backlog = target.length - revealedLen;
            if (backlog > 0) {
              const fraction = Math.min(1, dt / smoothingMs);
              const reveal = Math.max(1, Math.ceil(backlog * fraction));
              revealedLen = Math.min(target.length, revealedLen + reveal);
              flushSync(() => {
                patch(botMsg.id, {
                  text: target.slice(0, revealedLen),
                  isTyping: false,
                });
              });
            }
            if (serverDone && revealedLen >= target.length) {
              rafId = null;
              drainResolve();
              return;
            }
            rafId = requestAnimationFrame(tick);
          };

          const ensureRendering = (): void => {
            if (smoothingMs <= 0 || rafId !== null) return;
            lastFrameTime = performance.now();
            rafId = requestAnimationFrame(tick);
          };

          const ingest = (chunk: string): void => {
            target += chunk;
            if (smoothingMs <= 0) {
              revealedLen = target.length;
              flushSync(() => {
                patch(botMsg.id, { text: target, isTyping: false });
              });
            } else {
              ensureRendering();
            }
          };

          try {
            for await (const ev of chatStream(
              config.apiBaseUrl ?? '',
              { message: text },
              ctrl.signal,
            )) {
              if (ev.event === 'token') {
                if (ev.data.partial) {
                  receivedAnyPartial = true;
                  ingest(ev.data.text);
                } else if (!receivedAnyPartial) {
                  ingest(ev.data.text);
                }
              } else if (ev.event === 'done') {
                // Backend sinaliza se a resposta tem conteúdo aproveitável.
                // Ausente (backend antigo) = mantém o comportamento anterior.
                if (ev.data?.has_content === false) hideActionsOnDone = true;
              } else if (ev.event === 'error') {
                throw new Error(ev.data.message ?? 'stream emitted error event');
              }
            }
          } finally {
            serverDone = true;
          }

          if (smoothingMs > 0 && rafId !== null) {
            await drainPromise;
          }

          patch(botMsg.id, {
            isStreaming: false,
            isTyping: false,
            ...(hideActionsOnDone && { hideActions: true }),
          });
          setStreaming(null, 'idle');
          streamSucceeded = true;
        } catch (err) {
          if (ctrl.signal.aborted) {
            setStreaming(null, 'idle');
            return;
          }
          if (!config.fallbackToNonStreaming) {
            handleFatalError(err);
            return;
          }
        }
      }

      if (streamSucceeded) return;

      try {
        const res = await api.sendText({ message: text, signal: ctrl.signal });
        const hideActions = shouldHideActions(res);
        patch(botMsg.id, {
          text: res.response,
          isStreaming: false,
          isTyping: false,
          ...(hideActions !== undefined && { hideActions }),
        });
        setStreaming(null, 'idle');
      } catch (err) {
        if (ctrl.signal.aborted) {
          setStreaming(null, 'idle');
          return;
        }
        handleFatalError(err);
      }

      function handleFatalError(err: unknown) {
        setStreaming(null, 'error');
        const isFatal = isServerOrNetworkError(err);
        if (isFatal && config.enableErrorOverlay) {
          remove(botMsg.id);
          showErrorOverlay(config.errorOverlayCountdownSec);
        } else if (isFatal) {
          patch(botMsg.id, {
            text: branding.errorMessage,
            isStreaming: false,
            isTyping: false,
            hideActions: true,
          });
        } else {
          remove(botMsg.id);
        }
      }
    },
    [
      api,
      append,
      patch,
      remove,
      setInputText,
      setStreaming,
      showErrorOverlay,
      branding.errorMessage,
      config.apiBaseUrl,
      config.enableStreaming,
      config.enableErrorOverlay,
      config.errorOverlayCountdownSec,
      config.fallbackToNonStreaming,
      config.streamSmoothingMs,
    ],
  );

  const sendAudio = useCallback(
    async (blob: Blob, mimeType: string, durationMs: number) => {
      // Object URL do próprio blob gravado, para o usuário reouvir o que enviou
      // (US 13335). Revogado pelo store em removeMessage/clearChat.
      const userMsg: UserAudioMessage = {
        id: uuid(),
        sender: 'user',
        kind: 'user-audio',
        text: `🎤 Áudio enviado · ${formatDuration(durationMs)}`,
        audioUrl: URL.createObjectURL(blob),
        durationMs,
        createdAt: Date.now(),
      };
      const botMsg: TextMessage = {
        id: uuid(),
        sender: 'bot',
        kind: 'text',
        text: '',
        createdAt: Date.now(),
        isStreaming: true,
        isTyping: true,
      };

      append(userMsg);
      append(botMsg);
      setStreaming(botMsg.id, 'streaming');

      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;

      try {
        const res = await api.sendAudio(blob, mimeType, ctrl.signal);
        const hideActions = shouldHideActions(res);
        patch(botMsg.id, {
          text: res.response,
          isStreaming: false,
          isTyping: false,
          ...(hideActions !== undefined && { hideActions }),
        });
        setStreaming(null, 'idle');
      } catch (err) {
        if (ctrl.signal.aborted) {
          setStreaming(null, 'idle');
          return;
        }
        setStreaming(null, 'error');
        const isFatal = isServerOrNetworkError(err);
        if (isFatal && config.enableErrorOverlay) {
          remove(botMsg.id);
          showErrorOverlay(config.errorOverlayCountdownSec);
        } else if (isFatal) {
          patch(botMsg.id, {
            text: branding.errorMessage,
            isStreaming: false,
            isTyping: false,
            hideActions: true,
          });
        } else {
          remove(botMsg.id);
        }
      }
    },
    [
      api,
      append,
      patch,
      remove,
      setStreaming,
      showErrorOverlay,
      branding.errorMessage,
      config.enableErrorOverlay,
      config.errorOverlayCountdownSec,
    ],
  );

  const generatePodcast = useCallback(
    async (sourceMessageId: string) => {
      const messages = store.getState().messages;
      const sourceIdx = messages.findIndex((m) => m.id === sourceMessageId);
      if (sourceIdx < 0) return;
      const source = messages[sourceIdx];
      if (source?.kind !== 'text' || source.sender !== 'bot') return;

      let topic = source.text;
      for (let i = sourceIdx - 1; i >= 0; i--) {
        const m = messages[i];
        if (m?.kind === 'text' && m.sender === 'user') {
          topic = m.text;
          break;
        }
      }

      podcastCtrlRef.current.get(sourceMessageId)?.abort();
      const ctrl = new AbortController();
      podcastCtrlRef.current.set(sourceMessageId, ctrl);

      setPodcastGen(sourceMessageId, 'generating');

      try {
        const res = await api.generatePodcast(
          { topic, context: source.text, signal: ctrl.signal },
          config.podcastTimeoutMs,
        );
        const podcastMsg: PodcastMessage = {
          id: uuid(),
          kind: 'podcast',
          sender: 'bot',
          createdAt: Date.now(),
          text: 'Clique em reproduzir para ouvir ou baixar o áudio.',
          audioUrl: res.audio_url,
          durationSec: res.duration_seconds,
          sourceMessageId,
        };
        append(podcastMsg);
        setPodcastGen(sourceMessageId, 'done');
      } catch (err) {
        if (ctrl.signal.aborted) {
          setPodcastGen(sourceMessageId, 'idle');
          return;
        }
        setPodcastGen(sourceMessageId, 'error');
        if (config.enableErrorOverlay && isServerOrNetworkError(err)) {
          showErrorOverlay(config.errorOverlayCountdownSec);
        }
      } finally {
        podcastCtrlRef.current.delete(sourceMessageId);
      }
    },
    [
      api,
      append,
      setPodcastGen,
      showErrorOverlay,
      store,
      config.podcastTimeoutMs,
      config.enableErrorOverlay,
      config.errorOverlayCountdownSec,
    ],
  );

  const clear = useCallback(() => {
    ctrlRef.current?.abort();
    ctrlRef.current = null;
    podcastCtrlRef.current.forEach((c) => {
      c.abort();
    });
    podcastCtrlRef.current.clear();
    resetSession();
    clearChat(makeWelcomeMessage(branding.welcomeMessage));
  }, [clearChat, branding.welcomeMessage]);

  return { sendText, sendAudio, generatePodcast, clear };
}

/**
 * Decide se as ações da resposta (ex.: "Gerar Podcast") devem ficar escondidas.
 *
 * `has_content: false` é o sinal novo do backend (US 13334): resposta sem
 * conteúdo aproveitável — busca vazia ou recusa do guardrail.
 * `hideActionButtons` é o campo legado, mantido por compatibilidade.
 * `undefined` = backend não opinou; preserva o comportamento anterior.
 */
function shouldHideActions(res: ChatResponse): boolean | undefined {
  if (res.has_content === false) return true;
  return res.hideActionButtons;
}

function isServerOrNetworkError(err: unknown): boolean {
  if (err instanceof ChatHttpError) return err.status >= 500;
  if (err instanceof TypeError) return true;
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return msg.includes('network') || msg.includes('failed to fetch');
  }
  return false;
}
